import React from 'react';
import { useNavigate } from 'react-router-dom';
import ICN from './pageIcons';
import {
    ext, fileClass, sourceCode, targetCodes, ownerInitials, avatarPalette,
    formatDue, dueClass, progressOf, statusFor, projectGroup, projectName,
} from './projectHelpers';

const TableRow = ({ p, checked, onCheck }) => {
    const navigate = useNavigate();
    const e = ext(p);
    const targets = targetCodes(p);
    const visible = targets.slice(0, 4);
    const extra = targets.length - visible.length;
    const owners = (p.collaborators || p.assignedTo || []).slice(0, 3);
    const status = statusFor(p);
    const pct = progressOf(p);
    return (
        <div
            className={`ptbl-row body wide ${checked ? 'checked' : ''}`}
            onClick={(evt) => {
                if (evt.target.closest('.sel')) return;
                navigate(`/dashboard/projects/${p._id}`);
            }}
        >
            <span className="sel" onClick={(evt) => { evt.stopPropagation(); onCheck(); }}>{ICN.check}</span>
            <div className="p-name">
                <span className={`fic ${fileClass(e)}`}>{e}</span>
                <span className="nm-wrap">
                    <b>{projectName(p)}</b>
                    <span>{projectGroup(p)}</span>
                </span>
            </div>
            <div className="p-langs">
                <span className="p-lang src">{sourceCode(p)} →</span>
                {visible.map((l) => <span key={l} className="p-lang">{l}</span>)}
                {extra > 0 && <span className="p-lang more">+{extra}</span>}
            </div>
            <div className="p-prog">
                <span className="bar"><span style={{ width: `${pct}%` }} /></span>
                <span className="pct">{pct}%</span>
            </div>
            <div className="p-owners">
                {owners.map((o, i) => (
                    <span key={i} className={`av-s ${avatarPalette(i)}`}>{ownerInitials(o, i)}</span>
                ))}
            </div>
            <div className={`p-due ${dueClass(p.deadline, status)}`}>{formatDue(p.deadline)}</div>
            <span className={`p-status ${status}`}>
                <span className="pulse" />{status}
            </span>
            <span className="more">{ICN.more}</span>
        </div>
    );
};

const TableView = ({ rows, allRowsCount, selected, toggleSel, toggleAll, page, setPage, pageSize }) => {
    const allChecked = rows.length > 0 && rows.every((r) => selected[r._id]);

    const groups = [];
    const seen = new Map();
    rows.forEach((r) => {
        const g = projectGroup(r);
        if (!seen.has(g)) {
            seen.set(g, groups.length);
            groups.push({ name: g, items: [] });
        }
        groups[seen.get(g)].items.push(r);
    });

    const totalPages = Math.max(1, Math.ceil(allRowsCount / pageSize));

    return (
        <div className="rs-card flat">
            <div className="ptbl-row head wide">
                <span className={`sel ${allChecked ? 'checked' : ''}`} onClick={toggleAll}>{ICN.check}</span>
                <span>Project</span>
                <span>Source → target</span>
                <span>Progress</span>
                <span>Owners</span>
                <span>Due</span>
                <span>Status</span>
                <span />
            </div>
            {groups.map((g) => (
                <React.Fragment key={g.name}>
                    <div className="rs-group">
                        <span>{g.name}</span>
                        <span className="gct">{g.items.length}</span>
                        <span className="gline" />
                    </div>
                    {g.items.map((p) => (
                        <TableRow key={p._id} p={p} checked={!!selected[p._id]} onCheck={() => toggleSel(p._id)} />
                    ))}
                </React.Fragment>
            ))}
            <div className="rs-pg">
                <span className="ct">
                    Showing <b>{rows.length}</b> of <b>{allRowsCount}</b> projects
                </span>
                <span className="spacer" />
                <button type="button" className="pg-btn" disabled={page === 0} onClick={() => setPage(page - 1)}>‹ Prev</button>
                {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => (
                    <span
                        key={i}
                        className={`pg-num ${i === page ? 'on' : ''}`}
                        onClick={() => setPage(i)}
                        role="button"
                        tabIndex={0}
                    >
                        {i + 1}
                    </span>
                ))}
                <button type="button" className="pg-btn" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>Next ›</button>
            </div>
        </div>
    );
};

export default TableView;
